<template>

    <Audio
        :src="src"
        :savePosition="true"
        ref="audio"
        v-slot="{togglePlay, playing, percentage, player, currentTime, timeRemaining, buffered, speed, muted, waiting}"
    >

        <!--
          Compact transport for the mini player (HAP-4052 redesign).

          The old one borrowed the full page player's two-row layout — a clock
          and a speed chip stacked in the left column, the mirror of that on the
          right, a 40px play button in the middle — inside a panel a third the
          height. On a phone that put a 24px speed chip and a 24px mute chip
          within a thumb's width of the transport.

          One thin scrub track across the top, then a single row: elapsed,
          transport, speed, remaining. Every control clears 40px.
        -->
        <div
            class="w-full relative py-3 cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ncfic-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 group/scrub"
            ref="progressBar"
            role="slider"
            :aria-valuenow="Math.round(percentage) || 0"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Seek"
            tabindex="0"
            @keydown="onScrubKey($event, player)"
            @mouseover="handleHover"
            @mousemove="handleHover"
            @mouseout="clearHover"
            @click.stop="seekFromEvent($event, player)"
        >
            <div class="absolute inset-x-0 h-[3px] rounded-full bg-gray-200 dark:bg-gray-800" />
            <div class="absolute inset-x-0 h-[3px] rounded-full bg-gray-300 dark:bg-gray-700" :style="`width: ${buffered || 0}%`" />
            <div class="absolute inset-x-0 h-[3px] rounded-full bg-ncfic-blue-500" :style="`width: ${percentage || 0}%`" />
            <div
                class="w-2.5 h-2.5 bg-ncfic-blue-500 absolute -mt-[3.5px] rounded-full ring-2 ring-white dark:ring-gray-900 transition-transform group-hover/scrub:scale-125"
                :style="`left: calc(${percentage || 0}% - 0.3125rem);`"
            />
        </div>

        <div class="flex items-center justify-between gap-2">
            <span class="w-11 shrink-0 text-2xs tabular-nums text-gray-400 dark:text-gray-600">{{ currentTime || '0:00' }}</span>

            <div class="flex items-center gap-1.5">
                <Skip
                    :player="player"
                    :duration="-15"
                    class="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                />
                <button
                    @click="togglePlay"
                    v-press
                    :aria-label="playing ? 'Pause' : 'Play'"
                    :title="playing ? 'Pause' : 'Play'"
                    class="w-11 h-11 rounded-full bg-ncfic-blue-800 dark:bg-ncfic-blue-200 text-white dark:text-gray-900 flex items-center justify-center hover:bg-ncfic-blue-900 dark:hover:bg-white transition-colors"
                >
                    <i
                        :class="waiting && playing ? 'fal fa-spinner-third fa-spin' : (playing ? 'fa fa-pause' : 'fa fa-play ml-0.5')"
                        aria-hidden="true"
                    />
                </button>
                <Skip
                    :player="player"
                    :duration="15"
                    class="w-10 h-10 rounded-full flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                />
            </div>

            <div class="shrink-0 flex flex-col items-end gap-1">
                <span class="text-2xs tabular-nums text-gray-400 dark:text-gray-600">-{{ timeRemaining }}</span>
                <div class="flex items-center gap-1">
                    <!-- Mute was a working control in the old transport and the
                         redesign dropped it. It is the only volume affordance the
                         mini player has: without it, silencing a sermon means
                         closing the player, which also throws away the position. -->
                    <Mute
                        :player="player"
                        :muted="muted"
                        :title="muted ? 'Unmute' : 'Mute'"
                        class="h-6 w-7 rounded-full border border-gray-200 dark:border-gray-800 text-3xs text-gray-400 dark:text-gray-500 hover:border-ncfic-blue-500 hover:text-ncfic-blue-900 dark:hover:text-gray-200 transition-colors"
                    />
                    <Speed
                        :player="player"
                        :speed="speed"
                        title="Playback speed"
                        class="h-6 px-2 rounded-full border border-gray-200 dark:border-gray-800 text-3xs font-semibold tabular-nums text-gray-400 dark:text-gray-500 hover:border-ncfic-blue-500 hover:text-ncfic-blue-900 dark:hover:text-gray-200 transition-colors"
                    />
                </div>
            </div>
        </div>
    </Audio>

</template>

<script>
import Audio from '../audio/audio.vue'
import Skip from '../audio/skip.vue'
import Speed from '../audio/speed.vue'
import Mute from '../audio/mute.vue'

export default {
    props: {src: {type: String, required: true}},
    components: {Audio, Skip, Speed, Mute},
    data() {
        return {
            hoverPosition: null,
        }
    },
    mounted() {
        // The handoff: the page player paused on navigate, this one resumes.
        // `play()` returns a promise that rejects if the browser declines
        // (autoplay policy, or the element being torn down mid-call) — it was
        // unhandled, which surfaces as an "Uncaught (in promise)" NotAllowedError
        // in the console on every blocked handoff.
        this.$refs.audio?.play?.().catch(() => {})
    },
    methods: {
		// The track advertises `role="slider"` and a value, so it has to be
		// reachable and operable — announcing a control a keyboard user cannot
		// touch is worse than advertising nothing. Mirrors resources/audio.vue.
		onScrubKey($event, player) {
			if (!player?.duration) return
			const step = $event.shiftKey ? 60 : 15
			if ($event.key === 'ArrowRight') player.currentTime = Math.min(player.duration, player.currentTime + step)
			else if ($event.key === 'ArrowLeft') player.currentTime = Math.max(0, player.currentTime - step)
			else if ($event.key === 'Home') player.currentTime = 0
			else return
			$event.preventDefault()
		},
		// Seek from the CLICK, not from `hoverPosition`.
		//
		// `hoverPosition` is only ever written by mousemove, and `clearHover()`
		// resets it to 0 rather than null. So any activation that isn't preceded
		// by a live hover seeks to 0:00 and restarts the sermon: a keyboard or
		// screen-reader activation of the (now focusable, role="slider") track,
		// a touch tap, or simply a `mouseout` landing between the last mousemove
		// and the click as the pointer crosses off the 4px track onto padding.
		// The event carries the position; use it, and fall back to the hover
		// value only when there is no coordinate (a synthetic click).
		seekFromEvent($event, player) {
			if (!player?.duration) return
			const percentage = this.positionFrom($event)
			if (percentage == null) return
			player.currentTime = player.duration * (percentage / 100)
		},
		positionFrom($event) {
			const track = this.$refs.progressBar
			if (!track) return null
			const rect = track.getBoundingClientRect()
			if (!rect.width) return null
			// A synthetic/keyboard click reports clientX 0; fall back to the last
			// hovered position if there is one, otherwise do nothing.
			const clientX = $event?.clientX
			if (!clientX) return this.hoverPosition || null
			return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100))
		},
		handleHover($event) {
			this.hoverPosition = this.positionFrom($event)
		},
		clearHover() {
			this.hoverPosition = null
		},
    },
}
</script>
